import { createClient } from '@supabase/supabase-js';
import { logger } from '@shared/utils/logger';
import { viteEnvTrim } from './viteEnvTrim';

const supabaseUrl = viteEnvTrim(import.meta.env.VITE_SUPABASE_URL);
const supabaseAnonKey = viteEnvTrim(import.meta.env.VITE_SUPABASE_ANON_KEY);

// Comprobación de las variables de entorno antes de crear el cliente
if (!supabaseUrl) {
  throw new Error('VITE_SUPABASE_URL no está definida en el entorno.');
}
if (!supabaseAnonKey) {
  throw new Error('VITE_SUPABASE_ANON_KEY no está definida en el entorno.');
}

// 🔐 CONFIGURACIÓN DE SUPABASE CLIENT
// 
// ARQUITECTURA DE SESIÓN COMPARTIDA:
// 
// DEV: Todas las apps corren bajo el mismo origin vía proxy (localhost:5173)
//   - Accounts: http://localhost:5173/accounts
//   - Houseme: http://localhost:5173/houseme  
//   - Tienda: http://localhost:5173/tienda
//   - Empresa: http://localhost:5173
//   - localStorage se comparte automáticamente en el mismo origin
//   - NO usar cookies cross-port (no funciona en navegadores)
// 
// PROD: Cada app tiene su dominio, pero comparten localStorage del dominio base
//   - Accounts: https://accounts.elfom.com
//   - Houseme: https://houseme.app
//   - Mismo dominio base = localStorage compartido
// 
// DECISIÓN ARQUITECTÓNICA:
// - Houseme NO debe correr en puerto propio en DEV (5177)
// - Todas las apps deben accederse vía proxy: npm run dev:core
// - Sin hacks de cookies cross-port - solo mismo origin

// 🔑 STORAGE KEY EXPLÍCITO Y COMPARTIDO
// IMPORTANTE: Este key DEBE ser el mismo en todas las apps para compartir sesión
export const SUPABASE_STORAGE_KEY = `sb-${supabaseUrl.replace(/https?:\/\//, '').replace(/\./g, '-').split('/')[0]}-auth-token`;

// 🔒 SINGLETON ABSOLUTO: Evitar múltiples instancias de GoTrueClient
// Cuando se salta del navegador a PWA/App, pueden crearse múltiples instancias
// Verificamos si ya existe una instancia compartida antes de crear una nueva
const GLOBAL_SUPABASE_KEY = '__SUPABASE_CLIENT_SINGLETON__';

let supabaseInstance: ReturnType<typeof createClient> | null = null;

function getOrCreateSupabaseClient() {
  // Si ya existe una instancia en memoria, reutilizarla
  if (supabaseInstance) {
    logger.debug('[SUPABASE] Reutilizando instancia existente (singleton)');
    return supabaseInstance;
  }

  // Verificar si hay una instancia en window (compartida entre módulos)
  if (typeof window !== 'undefined' && (window as any)[GLOBAL_SUPABASE_KEY]) {
    logger.debug('[SUPABASE] Reutilizando instancia de window (singleton)');
    supabaseInstance = (window as any)[GLOBAL_SUPABASE_KEY];
    return supabaseInstance;
  }

  // Crear nueva instancia solo si no existe ninguna
  logger.debug('[SUPABASE] Creando nueva instancia (primera vez)');
  supabaseInstance = createClient(supabaseUrl, supabaseAnonKey, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      storageKey: SUPABASE_STORAGE_KEY, // 🔑 STORAGE KEY EXPLÍCITO Y COMPARTIDO
    },
    global: {
      headers: {},
    },
  });

  // Guardar en window para compartir entre módulos
  if (typeof window !== 'undefined') {
    (window as any)[GLOBAL_SUPABASE_KEY] = supabaseInstance;
  }

  return supabaseInstance;
}

/** Resuelve cuando el cliente está listo (evita race en primer getSession). */
// ✅ PRIORIDAD: Resolver ANTES que cualquier hook intente usar la BD
export const whenSupabaseReady: Promise<void> = (() => {
  if (typeof window === 'undefined') {
    return Promise.resolve();
  }
  
  // Resolver inmediatamente en el siguiente microtask (más rápido que setTimeout)
  return new Promise<void>((resolve) => {
    // Usar queueMicrotask para máxima prioridad
    queueMicrotask(() => {
      // Asegurar que el cliente esté inicializado antes de resolver
      getOrCreateSupabaseClient();
      resolve();
    });
  });
})();

// ✅ SINGLETON: Exportar instancia única
export const supabase = getOrCreateSupabaseClient();

// ============================================
// SIMULATION CONTEXT SUPPORT
// ============================================
// Interceptar requests para agregar headers de simulación automáticamente
// Esto permite que el contexto de simulación se propague a todas las queries

// Lazy import para evitar dependencias circulares
let simulationContextService: any = null;

const getSimulationHeaders = (): Record<string, string> => {
  // Lazy load del servicio
  if (!simulationContextService) {
    try {
      simulationContextService = require('@shared/services/simulationContextService').simulationContextService;
    } catch (error) {
      // Si no está disponible, retornar headers vacíos
      return {};
    }
  }

  try {
    return simulationContextService.getHeaders();
  } catch (error) {
    return {};
  }
};

// Interceptar requests de Supabase para agregar headers de simulación
// ✅ MANEJO DE ABORTERROR: Si una petición es abortada (navegador → PWA), esperar 300ms y permitir retry
if (typeof window !== 'undefined') {
  const originalFetch = window.fetch;
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const simulationHeaders = getSimulationHeaders();
    const headers = new Headers(init?.headers);

    // Ensure Supabase requests always have an Authorization header.
    // The Supabase JS client should handle this, but as a safety net for
    // unauthenticated (anon) callers on public pages we fall back to the anon key.
    const reqUrl = typeof input === 'string' ? input : (input as Request).url ?? '';
    if (reqUrl.includes(supabaseUrl) && !headers.has('Authorization') && supabaseAnonKey) {
      headers.set('Authorization', `Bearer ${supabaseAnonKey}`);
    }

    Object.entries(simulationHeaders).forEach(([key, value]) => {
      headers.set(key, value as string);
    });

    try {
      return await originalFetch(input, {
        ...init,
        headers,
      });
    } catch (error) {
      // ✅ CRÍTICO: Manejar AbortError cuando se salta del navegador a PWA/App
      // El error puede venir como AbortError, DOMException con mensaje "signal is aborted", etc.
      const isAbortError = 
        error instanceof Error && (
          error.name === 'AbortError' ||
          error.message?.toLowerCase().includes('abort') ||
          error.message?.toLowerCase().includes('signal is aborted')
        ) ||
        (error as any)?.name === 'AbortError' ||
        (error as any)?.message?.toLowerCase()?.includes('abort');

      if (isAbortError) {
        const isAuthRequest = reqUrl.includes('/auth/v1/');
        logger.debug('[SUPABASE] AbortError detectado (probable navegador → PWA)');

        await new Promise(resolve => setTimeout(resolve, 300));

        // ✅ Auth: reintentar — status 0 rompe refresh de token en GoTrue
        if (isAuthRequest) {
          try {
            return await originalFetch(input, {
              ...init,
              headers,
            });
          } catch (retryError) {
            logger.warn('[SUPABASE] Retry auth fetch falló tras AbortError');
            throw retryError;
          }
        }

        return new Response(null, {
          status: 0,
          statusText: 'Aborted (browser to PWA transition)',
        });
      }

      // Re-lanzar otros errores normalmente
      throw error;
    }
  };
}

// ============================================
// AUTH UTILITIES
// ============================================

export const DEBUG_AUTH = import.meta.env.VITE_DEBUG_AUTH === 'true';

export const logAuth = (message: string, data?: any) => {
  if (DEBUG_AUTH) {
    logger.debug(`🔐 AUTH: ${message}`, data);
  }
};

// ============================================
// TYPES
// ============================================

export type UserRole =
  | 'gerente_general'
  | 'gerente_operaciones'
  | 'gerente_comercial'
  | 'gerente_rrhh'
  | 'gerente_finanzas'
  | 'supervisor'
  | 'coordinador'
  | 'empleado'
  | 'cliente'
  | 'proveedor'
  | 'admin';

export type PortalType = 'empresa' | 'tienda' | 'houseme' | 'client';
